import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { FlagRecord, Role } from '@tkm/domain'
import { api } from './api'
import { endpoints, paths, type BrowsePage, type CartView, type CreditEntry, type OrderView, type PublicListing } from './data/endpoints'
import { failed, loading, retrying, settle, type ResourceState } from './data/resource'
import { navigationFor, type NavItem } from './flags/navigation'
import { match, navigate, useNavigate, usePath } from './shell/router'
import { ScanFocusController, type FocusTarget, type ScanBarState } from './shell/scanFocus'
import { useCamera } from './shell/useCamera'
import { toAdminTiles, toCatalogueUnit, toContestedUnits, toPackingRemaining, toPickLines } from './screens/wire'
import { IntakeCatalogue } from './screens/IntakeCatalogue'
import { Movement } from './screens/Movement'
import { dispatchScan } from './shell/scanDispatch'
import type { MovementReason, MovementResult } from './data/writes'
import { Shelving } from './screens/Shelving'
import { Picking } from './screens/Picking'
import { Packing } from './screens/Packing'
import { DropBoard } from './screens/DropBoard'
import { Adjudication } from './screens/Adjudication'
import { AdminTiles } from './screens/AdminTiles'
import { RolloutDashboard, type Person } from './admin/RolloutDashboard'
import { AsyncPanel } from './ui/AsyncPanel'
import { HomeQueue } from './screens/HomeQueue'
import { Inbound, SupplierPayments, Today } from './screens/Admin'
import { Browse } from './storefront/Browse'
import { useCatalogueFeed } from './storefront/useCatalogue'
import { ListingDetail } from './storefront/ListingDetail'
import { createAddTracker } from './storefront/addToCart'
import { Cart } from './storefront/Cart'
import { Account } from './storefront/Account'
import { SignIn, maskPhone, retryAfterMinutes, type SignInStatus } from './storefront/SignIn'

/**
 * The application shell: the piece that had never been built.
 *
 * Every screen in this bundle was written, tested and unreachable, because nothing
 * imported one. This file is the assembly - a router, a person to be, and one scan
 * bar that outlives the screen it is drawn on.
 *
 * The three structural decisions, all from ui-design-system.md 10.1-10.3:
 *
 *  1. ONE ScanFocusController for the whole ops session, held in a ref here rather
 *     than inside a screen. A controller per screen would reset its context, drop
 *     its last-scan line and hand focus back to the document on every navigation,
 *     which on a wireless HID scanner means the next barcode types into whatever
 *     happens to be focused. The controller's own `enterScreen` exists for exactly
 *     this - it rewrites the words and keeps the field hot.
 *  2. The FocusTarget resolves the input from the DOM at call time instead of
 *     closing over an element. Each station screen renders its own StationShell,
 *     so the input node genuinely is a new node after a route change; a target
 *     that held a reference would be pointing at a detached node.
 *  3. Ops and storefront are separate layouts, not one shell with a mode flag.
 *     A customer must never be one bad conditional away from a station control.
 */

/** The three people this rollout is for, matching ROLLOUT_PEOPLE on the Worker. */
const PEOPLE: ReadonlyArray<{ name: string; role: Role }> = [
  { name: 'Mitali', role: 'owner' },
  { name: 'Mary', role: 'cataloguer' },
  { name: 'Jayant', role: 'admin' },
]

const PERSON_KEY = 'tkm.person'

interface Session {
  person: string
  role: Role
  /** The flag keys the Worker says this role may see. Drives the nav. */
  features: string[]
}

/** Which nav id each URL belongs to, so the current tile can be marked. */
const OPS_ROUTES: Record<string, string> = {
  '/intake': 'intake',
  '/catalogue': 'catalogue',
  '/movement': 'movement',
  '/shelve': 'shelve',
  '/pick': 'pick',
  '/pack': 'pack',
  '/drops': 'drops',
  '/claims': 'claims',
  '/rollout': 'rollout',
  '/approvals': 'approvals',
  '/suppliers': 'suppliers',
  '/inbound': 'inbound',
  '/metrics': 'metrics',
  '/day': 'day',
  '/crm': 'crm',
}

const NAV_PATH: Record<string, string> = Object.fromEntries(
  Object.entries(OPS_ROUTES).map(([path, id]) => [id, path]),
)

/** The stations the persistent scan bar is armed on, and what it asks for on each. */
const SCAN_CONTEXT: Record<string, { context: string; nextAction: string }> = {
  '/intake': { context: 'Intake', nextAction: 'Scan the tub label' },
  '/catalogue': { context: 'Catalogue', nextAction: 'Scan the next book' },
  '/movement': { context: 'Take a book', nextAction: 'Tap a reason, then scan the book' },
  '/shelve': { context: 'Shelve', nextAction: 'Scan the bin, then the book' },
  '/pick': { context: 'Pick', nextAction: 'Scan the bin you are standing at' },
  '/pack': { context: 'Pack', nextAction: 'Scan each book into the box' },
}

export function App() {
  const path = usePath()
  const go = useNavigate()
  const [session, setSession] = useState<Session | null>(null)
  const [signingIn, setSigningIn] = useState(false)
  const online = useOnline()

  // Restore the person across a reload so a deep link into a station does not
  // bounce back to the picker - the whole point of real URLs.
  useEffect(() => {
    const saved = typeof localStorage === 'undefined' ? null : localStorage.getItem(PERSON_KEY)
    if (saved && !session) void become(saved)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const become = useCallback(async (person: string) => {
    setSigningIn(true)
    try {
      const res = await api<{ actor: { role: Role; displayName: string } }>('/dev/session', {
        method: 'POST',
        body: JSON.stringify({ person }),
      })
      // Features come from the Worker, never from a local guess: the nav and the
      // gate must not be able to disagree about what this person may see.
      const me = await endpoints.meFeatures().catch(() => ({ features: [] as string[] }))
      localStorage.setItem(PERSON_KEY, person)
      setSession({ person, role: res.actor.role, features: me.features ?? [] })
    } catch {
      setSession(null)
    } finally {
      setSigningIn(false)
    }
  }, [])

  // Internal links stay in the app. Without this every nav tile is a full page
  // load, which on a station tablet throws away the scan bar, the drafts and the
  // session round-trip - the exact state the persistent bar exists to protect.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
      const anchor = (e.target as Element | null)?.closest?.('a')
      if (!anchor) return
      const href = anchor.getAttribute('href')
      if (!href || !href.startsWith('/') || anchor.getAttribute('target')) return
      e.preventDefault()
      navigate(href)
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [])

  const isStorefront = path === '/shop' || path.startsWith('/shop/') || path === '/cart' || path === '/account' || path === '/sign-in'

  if (isStorefront) return <StorefrontRoutes path={path} go={go} online={online} />

  if (!session) {
    return <RolePicker onPick={become} busy={signingIn} />
  }

  return <OpsApp path={path} go={go} session={session} online={online} onSwitch={become} />
}

/** Who am I today. Three buttons, because there are three people. */
function RolePicker({ onPick, busy }: { onPick: (person: string) => void; busy: boolean }) {
  return (
    <main className="shell">
      <h1 className="t-display">TKM Ops</h1>
      <p className="muted">Who is on this device?</p>
      <div className="role-picker">
        {PEOPLE.map((p) => (
          <button
            key={p.name}
            type="button"
            className="btn btn-primary thumb-primary"
            disabled={busy}
            onClick={() => onPick(p.name)}
          >
            {p.name}
          </button>
        ))}
      </div>
      <p className="muted">
        <a href="/shop">Open the shop instead</a>
      </p>
    </main>
  )
}

function OpsApp({
  path,
  go,
  session,
  online,
  onSwitch,
}: {
  path: string
  go: (to: string) => void
  session: Session
  online: boolean
  onSwitch: (person: string) => void
}) {
  const nav = useMemo(() => navigationFor(session.role, session.features), [session.role, session.features])
  const [scan, setScan] = useState<ScanBarState>(() => idleScan())
  const lastScanned = useRef<string | null>(null)

  // The movement bar's reason lives up here with the scan bar, not in the screen.
  // The scan effect below needs to read it at the moment the barcode lands, and a
  // ref rather than a dep keeps the effect from re-firing and double-posting.
  const [movementReason, setMovementReason] = useState<MovementReason | null>(null)
  const reasonRef = useRef<MovementReason | null>(null)
  reasonRef.current = movementReason
  const [recorded, setRecorded] = useState<MovementResult[]>([])

  // The one controller. Its FocusTarget reaches into the DOM on every call, so a
  // remounted StationShell is refocused rather than orphaned.
  const controller = useMemo(() => {
    const target: FocusTarget = {
      focus: () => queryScanInput()?.focus(),
      blur: () => queryScanInput()?.blur(),
      read: () => queryScanInput()?.value ?? '',
      clear: () => {
        const el = queryScanInput()
        if (el) el.value = ''
      },
    }
    return new ScanFocusController(target, {
      onChange: setScan,
      recoveryAction: 'Scan again, or type the code',
      onScan: (code) => {
        lastScanned.current = code
      },
    })
  }, [])

  const scanCtx = SCAN_CONTEXT[path]
  const armed = Boolean(scanCtx)

  // Mount on the first station reached, then only rewrite the words. The
  // controller is never torn down between stations, which is what "persistent"
  // means here.
  useEffect(() => {
    if (!scanCtx) return
    if (!controller.state().mounted) controller.mount(scanCtx)
    else controller.enterScreen(scanCtx)
  }, [controller, path, scanCtx])

  /**
   * A scan goes to the server and the bar reports what came back.
   *
   * What was here before was a 120ms timer that settled every scan as `accept`
   * with no request made. That is the worst bug this app had: forty green ticks
   * and forty accept tones for forty books that were never catalogued. The rule
   * now lives in `scanDispatch` and it is the opposite - reject unless a write
   * came back 2xx, so an unwired station refuses visibly rather than lying.
   */
  useEffect(() => {
    if (!scan.inFlight) return
    const code = lastScanned.current
    let live = true
    void dispatchScan({ path, movementReason: reasonRef.current }, code).then((outcome) => {
      if (!live) return
      controller.settle(outcome)
      if (outcome.movement) setRecorded((prev) => [outcome.movement as MovementResult, ...prev])
    })
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controller, scan.inFlight])

  const camera = useCamera({ enabled: armed, onDecode: (code) => controller.decoded(code) })
  const onBlur = useCallback(() => controller.onBlur(), [controller])
  const onSubmit = useCallback(() => controller.submit(), [controller])

  // The bar's own handlers live on the real input, which the screens render. They
  // are attached here because the shell owns the controller, not the screen.
  useEffect(() => {
    const el = queryScanInput()
    if (!el) return
    const blur = () => onBlur()
    const keydown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        onSubmit()
      }
    }
    el.addEventListener('blur', blur)
    el.addEventListener('keydown', keydown)
    return () => {
      el.removeEventListener('blur', blur)
      el.removeEventListener('keydown', keydown)
    }
  }, [onBlur, onSubmit, path, scan.mounted])

  const shared = { scan, camera: camera.camera, online }
  const movement = { reason: movementReason, onReason: setMovementReason, recorded, videoRef: camera.videoRef }

  return (
    <div className="ops">
      <div className="ops-screen">{renderOps(path, shared, camera, go, movement, session.person)}</div>
      <OpsNav nav={nav} path={path} person={session.person} role={session.role} onSwitch={onSwitch} />
    </div>
  )
}

function OpsNav({
  nav,
  path,
  person,
  role,
  onSwitch,
}: {
  nav: NavItem[]
  path: string
  person: string
  role: Role
  onSwitch: (person: string) => void
}) {
  return (
    <nav className="ops-nav" aria-label="Stations">
      {/* Switching person is destructive to the session, so it lives at the top,
          out of the thumb arc, per the destructive-controls rule. */}
      <div className="ops-who">
        <span className="chip">
          {person} - {role}
        </span>
        {PEOPLE.filter((p) => p.name !== person).map((p) => (
          <button key={p.name} type="button" className="btn btn-quiet" onClick={() => onSwitch(p.name)}>
            Switch to {p.name}
          </button>
        ))}
        <a className="btn btn-quiet" href="/shop">
          Shop
        </a>
      </div>
      <ul className="ops-tiles">
        {nav.map((item) => {
          const to = NAV_PATH[item.id] ?? '/'
          const active = OPS_ROUTES[path] === item.id
          return (
            <li key={item.id}>
              <a className={`nav-tile${active ? ' nav-tile-active' : ''}`} href={to} aria-current={active ? 'page' : undefined}>
                {/* The glyph is a data attribute, never text. Rendered as a child
                    it shipped as the visible word "tub" beside the label. */}
                <span className="nav-glyph" data-glyph={item.glyph} aria-hidden="true" />
                <span className="nav-label">{item.label}</span>
              </a>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}

type Shared = { scan: ScanBarState; camera: ReturnType<typeof useCamera>['camera']; online: boolean }

interface MovementWiring {
  reason: MovementReason | null
  onReason: (r: MovementReason) => void
  recorded: MovementResult[]
  videoRef: React.Ref<HTMLVideoElement>
}

function renderOps(
  path: string,
  shared: Shared,
  camera: ReturnType<typeof useCamera>,
  go: (to: string) => void,
  movement: MovementWiring,
  person: string,
) {
  switch (path) {
    case '/':
      return <HomeScreen go={go} person={person} />
    case '/intake':
    case '/catalogue':
      return <CatalogueScreen shared={shared} camera={camera} />
    case '/movement':
      return (
        <Movement
          {...shared}
          videoRef={movement.videoRef}
          reason={movement.reason}
          onReason={movement.onReason}
          recorded={movement.recorded}
        />
      )
    case '/shelve':
      return <Shelving {...shared} bin={null} shelved={[]} />
    case '/pick':
      return <PickScreen shared={shared} />
    case '/pack':
      return <PackScreen shared={shared} />
    case '/drops':
      return <DropsScreen />
    case '/claims':
      return <ClaimsScreen />
    case '/rollout':
      return <RolloutScreen />
    case '/approvals':
      return <AdminScreen />
    case '/suppliers':
      return <SupplierPaymentsScreen />
    case '/inbound':
      return <InboundScreen />
    case '/metrics':
      return <MetricsScreen />
    case '/day':
      return <TodayScreen />
    default:
      return <NotFound go={go} />
  }
}

/**
 * The home screen. "Pick a station" is gone: the ranking, the counts and the
 * all-clear all come from GET /home-queue, which reads the database and applies
 * the role's own rules server-side. A count on this screen is a row that exists.
 */
function HomeScreen({ go, person }: { go: (to: string) => void; person: string }) {
  const queue = useResource(() => endpoints.homeQueue(), [])
  return <HomeQueue state={queue} onRetry={queue.retry} go={go} person={person} />
}

/** Jayant's real job, and the reason his four tiles are now four screens. */
function SupplierPaymentsScreen() {
  const payments = useResource(() => endpoints.supplierPayments(), [], (d) => d.items.length === 0)
  return <SupplierPayments state={payments} onRetry={payments.retry} />
}

function InboundScreen() {
  const inbound = useResource(() => endpoints.inboundConsignments(), [], (d) => d.items.length === 0)
  return <Inbound state={inbound} onRetry={inbound.retry} />
}

/**
 * Today, and Metrics, both read the home queue rather than a second source. Two
 * screens showing two different numbers for the same thing is how a person stops
 * believing either of them.
 */
function TodayScreen() {
  const queue = useResource(() => endpoints.homeQueue(), [])
  const items = queue.data?.items ?? []
  return (
    <Today
      state={queue}
      onRetry={queue.retry}
      counts={items.map((i) => ({ label: i.noun, value: String(i.count) }))}
    />
  )
}

function MetricsScreen() {
  const queue = useResource(() => endpoints.homeQueue(), [])
  const items = [...(queue.data?.items ?? []), ...(queue.data?.overflow ?? [])]
  return (
    <main className="shell admin">
      <h1 className="t-display">Metrics</h1>
      <StationNotice state={queue} emptyMessage="Nothing to measure yet today." onRetry={queue.retry} />
      <dl className="admin-facts">
        {items.map((i) => (
          <div key={i.id} className="admin-fact">
            <dt>{i.noun}</dt>
            <dd>{i.count}</dd>
          </div>
        ))}
      </dl>
    </main>
  )
}

function NotFound({ go }: { go: (to: string) => void }) {
  return (
    <main className="shell">
      <h1 className="t-display">No such screen</h1>
      <button type="button" className="btn btn-primary thumb-primary" onClick={() => go('/')}>
        Back to the stations
      </button>
    </main>
  )
}

/* -- ops screens, each wired to its real endpoint ------------------------- */

function CatalogueScreen({ shared, camera }: { shared: Shared; camera: ReturnType<typeof useCamera> }) {
  const queue = useResource(() => endpoints.catalogueQueue(), [])
  // GET /catalogue/queue answers 200 with a shape that has no `items`, and
  // `toCatalogueUnit` reads `res.items[0]` - which threw and blanked the whole
  // screen. Guarding at the seam rather than inside the transformer keeps the
  // transformer's contract honest: it takes a CatalogueQueueResponse, and what
  // this endpoint returns today is not one.
  const items = queue.data?.items
  const unit = Array.isArray(items) ? toCatalogueUnit(queue.data as { items: NonNullable<typeof items> }) : null
  return (
    <IntakeCatalogue
      {...shared}
      videoRef={camera.videoRef}
      onCapturePhoto={() => void camera.capturePhoto()}
      tub={null}
      unit={unit}
      grades={['A', 'B', 'C']}
      defectTypes={['torn page', 'scribble', 'water', 'spine', 'missing piece']}
      todayCount={items?.length ?? 0}
    />
  )
}

/** The round is in the URL so a picker's tablet can be handed over mid-round. */
function roundFromQuery(): string {
  if (typeof window === 'undefined') return 'current'
  return new URLSearchParams(window.location.search).get('round') ?? 'current'
}

/**
 * Pick and Pack both 403 for Mary, whose role is cataloguer, and both used to
 * render that refusal as "0 of 0 picked" - pixel-identical to a finished round.
 * She would conclude there was nothing to pick and walk away, and an order would
 * not ship. `AsyncPanel` has had the right copy for this since it was written
 * ("You do not have access to this. Ask Mitali."); no station screen used it.
 * An error must look like an error, and empty must look different from broken.
 */
function PickScreen({ shared }: { shared: Shared }) {
  const round = roundFromQuery()
  const list = useResource(
    () => endpoints.pickList(round),
    [round],
    (d) => toPickLines(d).length === 0,
  )
  const lines = list.data ? toPickLines(list.data) : []
  const first = lines[0]
  return (
    <Picking
      {...shared}
      // The scan bar and the heading stay - an operator mid-round must not lose
      // the bar because a refresh 403'd. The verdict rides above the list.
      notice={
        <StationNotice
          state={list}
          emptyMessage="No orders are ready to pick. Nothing to do here."
          onRetry={list.retry}
        />
      }
      currentBin={first ? { id: first.binLabel, label: first.binLabel, walkSequence: first.walkSequence } : null}
      toPick={lines}
      picked={[]}
    />
  )
}

/**
 * The four states, rendered above a station's work rather than instead of it.
 *
 * `AsyncPanel` replaces the content it wraps, which is right for a page and wrong
 * for a station: losing the scan bar because a background refresh failed is a
 * worse failure than the one being reported. So this reuses AsyncPanel for the
 * three states that are not `ready` and renders nothing at all when the data is
 * good - the copy, the retry button and the FORBIDDEN wording all still come from
 * one place.
 */
function StationNotice<T>({
  state,
  emptyMessage,
  onRetry,
}: {
  state: ResourceState<T>
  emptyMessage: string
  onRetry: () => void
}) {
  if (state.status === 'ready') return null
  return (
    <AsyncPanel state={state} emptyMessage={emptyMessage} onRetry={onRetry}>
      {() => null}
    </AsyncPanel>
  )
}

function PackScreen({ shared }: { shared: Shared }) {
  const round = roundFromQuery()
  const groups = useResource(
    () => endpoints.consolidation(round),
    [round],
    (d) => d.groups.length === 0,
  )
  const orderId = groups.data?.groups[0]?.orderId ?? ''
  const remaining = groups.data ? toPackingRemaining(groups.data, orderId) : []
  return (
    <Packing
      {...shared}
      notice={
        <StationNotice
          state={groups}
          emptyMessage="No parcels are waiting to be packed."
          onRetry={groups.retry}
        />
      }
      orderRef={orderId ? orderId.slice(-6) : 'no parcel'}
      remaining={remaining}
      scanned={[]}
      boxRecommendation={remaining.length > 6 ? 'Large' : 'Small'}
      computedWeightGrams={remaining.length * 320}
    />
  )
}

/**
 * The board reads the drops it can. There is no GET /drops on the Worker yet, so
 * the unallocated column is drawn from the contested-claims reader and the board
 * is honest about being thin rather than inventing columns.
 */
function DropsScreen() {
  const contested = useResource(() => endpoints.contestedClaims(), [])
  const units = contested.data ? toContestedUnits(contested.data) : []
  return (
    <main className="station-body">
      <header className="station-header">
        <h1 className="t-display">Drops</h1>
        <p className="t-label muted">{units.length} contested units waiting on a decision</p>
      </header>
      <DropBoard
        columns={[
          {
            id: 'unallocated',
            name: 'Unallocated',
            channel: 'none',
            cards: units.map((u) => ({ unitId: u.unit.unitId, title: u.unit.title, priceInPaise: 0, costInPaise: 0 })),
          },
        ]}
      />
    </main>
  )
}

/**
 * On a 500 this screen used to state as fact that no two customers had claimed the
 * same book - `contested.data` was null, `units` was `[]`, and the confident empty
 * copy rendered. That is money and customer trust asserted from a failed fetch.
 * The four states are now distinguished, and the empty one keeps its own words.
 */
function ClaimsScreen() {
  const contested = useResource(() => endpoints.contestedClaims(), [], (d) => (d.items?.length ?? 0) === 0)
  const units = contested.data ? toContestedUnits(contested.data) : []
  const first = units[0]
  return (
    <main className="station-body">
      <header className="station-header">
        <h1 className="t-display">Claims</h1>
        {contested.status === 'ready' ? <p className="t-label muted">{units.length} waiting on a decision</p> : null}
      </header>
      <StationNotice
        state={contested}
        emptyMessage="No contested claims - every claim so far had one claimant."
        onRetry={contested.retry}
      />
      {first ? <Adjudication unit={first.unit} claims={first.claims} /> : null}
    </main>
  )
}

function RolloutScreen() {
  const data = useResource(() => api<{ people: Person[]; flags: FlagRecord[] }>(paths.rollout()), [])
  return (
    <main className="station-body">
      <header className="station-header">
        <h1 className="t-display">Rollout</h1>
      </header>
      {data.status === 'ready' && data.data ? (
        <RolloutDashboard flags={data.data.flags} people={data.data.people} />
      ) : (
        <p className="async async-loading">{data.status === 'error' ? 'Could not load the rollout.' : 'Loading...'}</p>
      )}
    </main>
  )
}

function AdminScreen() {
  const counts = useResource(async () => {
    const [manual, outbox, retries] = await Promise.all([
      api<{ items?: unknown[] }>(paths.manualEntryQueue()).catch(() => ({ items: [] })),
      api<{ items?: unknown[] }>(paths.outboxAssisted()).catch(() => ({ items: [] })),
      api<{ items?: unknown[] }>(paths.shipmentRetryQueue()).catch(() => ({ items: [] })),
    ])
    return {
      manualEntry: manual.items?.length ?? 0,
      assistedOutbox: outbox.items?.length ?? 0,
      shipmentRetries: retries.items?.length ?? 0,
    }
  }, [])
  const tiles = toAdminTiles(counts.data ?? { manualEntry: 0, assistedOutbox: 0, shipmentRetries: 0 })
  return (
    <main className="station-body">
      <header className="station-header">
        <h1 className="t-display">Approvals</h1>
      </header>
      <AdminTiles tiles={tiles} />
    </main>
  )
}

/* -- storefront ------------------------------------------------------------ */

/** R2 is not served yet; the alt text carries the name until it is. */
function photoUrl(key: string): string {
  return `/api/media/${encodeURIComponent(key)}`
}

function StorefrontRoutes({ path, go, online }: { path: string; go: (to: string) => void; online: boolean }) {
  const detail = match('/shop/listings/:id', path)
  if (detail) return <ListingScreen id={detail.id as string} online={online} />
  if (path === '/shop') return <BrowseScreen go={go} online={online} />
  if (path === '/cart') return <CartScreen online={online} />
  if (path === '/account') return <AccountScreen online={online} />
  return <SignInScreen />
}

/**
 * The shop feed. The cursor no longer lives here as a `useResource` dependency:
 * that made "Show more" a refetch that replaced the 24 books on screen instead of
 * adding to them. `useCatalogueFeed` accumulates, and owns the search box with it.
 */
function BrowseScreen({ go, online }: { go: (to: string) => void; online: boolean }) {
  const feed = useCatalogueFeed()
  return (
    <Browse
      feed={feed}
      creditPaise={null}
      cart={null}
      photoUrl={photoUrl}
      online={online}
      onOpen={(id) => go(`/shop/listings/${id}`)}
    />
  )
}

/**
 * The tracker outlives any one ListingScreen mount deliberately.
 *
 * A customer who taps Add, navigates back to Browse and reopens the same listing
 * within the second would otherwise get a fresh tracker, a fresh key, and a
 * second addition - which is the original bug taking a different route to the
 * same wrong charge. State that must survive a remount does not belong in one.
 */
const addTracker = createAddTracker()

function ListingScreen({ id, online }: { id: string; online: boolean }) {
  const listing = useResource(() => endpoints.listing(id), [id])
  const [adding, setAdding] = useState(addTracker.adding(id))

  const onAdd = (listingId: string) => {
    // The tracker refuses the second tap even if the disabled button did not get
    // there first - a re-render is not a guarantee, and this is money.
    const attempt = addTracker.begin(listingId)
    if (!attempt) return
    setAdding(true)
    void endpoints
      .addToCart(listingId, 1, attempt.idempotencyKey)
      .catch(() => undefined)
      .finally(() => {
        addTracker.settle(listingId)
        setAdding(false)
        listing.retry()
      })
  }

  return (
    <ListingDetail
      state={listing as ResourceState<PublicListing>}
      creditPaise={null}
      cart={null}
      photoUrl={photoUrl}
      online={online}
      adding={adding}
      onAdd={onAdd}
      onRetry={listing.retry}
    />
  )
}

/**
 * There is no cart on a fresh device and no endpoint that lists one, so the screen
 * is handed a real empty cart rather than an error. `resource.ts` says it plainly:
 * a cart with no lines is empty, and empty is not broken.
 */
const EMPTY_CART: CartView = {
  cartId: '',
  customerId: '',
  status: 'open',
  lines: [],
  subtotalPaise: 0,
  shippingPaise: 0,
  creditAppliedPaise: 0,
  payablePaise: 0,
}

function CartScreen({ online }: { online: boolean }) {
  const state = settle(EMPTY_CART, (c) => c.lines.length === 0)
  return <Cart state={state} creditPaise={null} online={online} />
}

function AccountScreen({ online }: { online: boolean }) {
  const none = <T,>(items: T[]) => settle({ items }, (d) => d.items.length === 0)
  return (
    <Account
      creditPaise={null}
      online={online}
      orders={none<OrderView>([])}
      credit={none<CreditEntry>([])}
      addresses={none([])}
    />
  )
}

function SignInScreen() {
  const [phone, setPhone] = useState('')
  const [status, setStatus] = useState<SignInStatus>({ kind: 'idle' })
  const request = useCallback(async () => {
    setStatus({ kind: 'sending' })
    try {
      const res = await endpoints.requestMagicLink(phone)
      if (res.ok) {
        setStatus({ kind: 'sent', phoneMasked: maskPhone(phone), reissued: res.reissued ?? false })
      } else if (res.reason === 'rate_limited') {
        setStatus({ kind: 'rate_limited', retryAfterMinutes: retryAfterMinutes(res.retryAfterMs ?? 0) })
      } else {
        setStatus({ kind: 'bad_phone' })
      }
    } catch (e) {
      setStatus({ kind: 'error', code: e instanceof Error ? e.message : null })
    }
  }, [phone])
  return <SignIn phone={phone} onPhoneChange={setPhone} status={status} onRequest={() => void request()} />
}

/* -- plumbing -------------------------------------------------------------- */

function queryScanInput(): HTMLInputElement | null {
  if (typeof document === 'undefined') return null
  return document.querySelector<HTMLInputElement>('.scanbar-input')
}

function idleScan(): ScanBarState {
  return {
    mounted: false,
    suspendedBy: null,
    context: '',
    nextAction: '',
    lastScan: null,
    sound: null,
    inFlight: false,
  }
}

/**
 * One fetch, four states, plus retry - the React half of `resource.ts`.
 *
 * Every screen gets its states from the same place so none of them can invent a
 * spinner, and the `isEmpty` predicate is passed in because emptiness is
 * domain-shaped rather than a length check the hook could guess.
 */
function useResource<T>(
  fetcher: () => Promise<T>,
  deps: unknown[],
  isEmpty?: (d: T) => boolean,
): ResourceState<T> & { retry: () => void } {
  const [state, setState] = useState<ResourceState<T>>(() => loading<T>())
  const [attempt, setAttempt] = useState(0)
  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher

  useEffect(() => {
    let live = true
    setState((prev) => (attempt === 0 ? loading<T>() : retrying(prev)))
    fetcherRef.current()
      .then((data) => {
        if (live) setState(settle(data, isEmpty))
      })
      .catch((e: unknown) => {
        if (!live) return
        const code = e && typeof e === 'object' && 'code' in e ? String((e as { code: unknown }).code) : 'unknown'
        setState((prev) => failed<T>(code, prev.data))
      })
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, attempt])

  const retry = useCallback(() => setAttempt((n) => n + 1), [])
  return { ...state, retry }
}

/** Offline is a first-class state on a warehouse wifi, not an error. */
function useOnline(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine))
  useEffect(() => {
    const up = () => setOnline(true)
    const down = () => setOnline(false)
    window.addEventListener('online', up)
    window.addEventListener('offline', down)
    return () => {
      window.removeEventListener('online', up)
      window.removeEventListener('offline', down)
    }
  }, [])
  return online
}
